<template>
  <v-dialog v-model="dialogModel" max-width="1200px">
    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <span class="headline">Đơn hàng cùng shop: {{ shopLabel }}</span>
        <v-btn icon @click="dialogModel = false">
          <v-icon>fa-times</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dense outlined class="mb-4">
          {{ error }}
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="pagination"
          :server-items-length="totalRows"
          :footer-props="footerProps"
          :loading="loading"
          :mobile-breakpoint="100"
          class="elevation-0 shop-orders-table"
        >
          <template v-slot:item.ID="{ item }">
            <a href="javascript:void(0)" class="shop-orders-link" @click="openOrder(item.ID)">
              {{ getDisplayId(item) }}
            </a>
          </template>

          <template v-slot:item.image_origin="{ item }">
            <div class="shop-orders-image-wrap">
              <v-img
                v-if="item.image_origin"
                :src="proxyImage(item.image_origin)"
                width="56"
                height="56"
                class="shop-orders-image"
              ></v-img>
              <div v-else class="shop-orders-image-placeholder">No image</div>
            </div>
          </template>

          <template v-slot:item.Status="{ item }">
            <v-chip
              x-small
              label
              :color="getStatusColor(item.Status)"
              text-color="white"
            >
              {{ item.Status | order_status }}
            </v-chip>
          </template>

          <template v-slot:item.CustomerName="{ item }">
            {{ item.CustomerName || item.Username || "—" }}
          </template>

          <template v-slot:item.CreatedDate="{ item }">
            {{ item.CreatedDate ? $options.filters.display_date(item.CreatedDate, 'DD/MM/YYYY HH:mm') : '—' }}
          </template>

          <template v-slot:no-data>
            <div class="shop-orders-empty">Không có đơn hàng nào cùng shop.</div>
          </template>
        </v-data-table>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="dialogModel = false">Đóng</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash';
import configs from '@/configs';
import { proxyImage } from '@/commons/utils';
import { request_header, request_payload } from '@/commons/hpod';

export default {
  name: 'ShopOrdersPopup',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    shopId: {
      type: [Number, String],
      default: null,
    },
    shopLabel: {
      type: String,
      default: 'Shop',
    },
  },

  data() {
    return {
      proxyImage,
      loading: false,
      error: '',
      items: [],
      totalRows: 0,
      isResettingPagination: false,
      pagination: {
        ...configs.PAGINATION,
        sortBy: ['CreatedDate'],
        sortDesc: [true],
      },
      footerProps: _.cloneDeep(configs.ITEMS_PER_PAGE),
    };
  },

  computed: {
    dialogModel: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    headers() {
      return [
        { text: 'Mã đơn', value: 'ID', sortable: false, width: '140px' },
        { text: 'Hình ảnh', value: 'image_origin', sortable: false, width: '100px' },
        { text: 'Trạng thái', value: 'Status', sortable: false, width: '130px' },
        { text: 'Khách hàng', value: 'CustomerName', sortable: false, width: '180px' },
        { text: 'Ngày lên đơn', value: 'CreatedDate', sortable: false, width: '180px' },
      ];
    },
    token() {
      return this.$store.state.authentication.token;
    },
    normalizedShopId() {
      return this.shopId === '' || this.shopId === null || this.shopId === undefined
        ? null
        : String(this.shopId);
    },
  },

  watch: {
    dialogModel(val) {
      if (val) {
        this.resetAndFetch();
      }
    },
    shopId() {
      if (this.dialogModel) {
        this.resetAndFetch();
      }
    },
    pagination: {
      deep: true,
      handler() {
        if (this.dialogModel && !this.isResettingPagination) {
          this.fetchOrders();
        }
      },
    },
  },

  methods: {
    resetAndFetch() {
      this.error = '';
      this.items = [];
      this.totalRows = 0;
      this.isResettingPagination = true;
      this.pagination = {
        ...this.pagination,
        page: 1,
      };
      this.$nextTick(() => {
        this.isResettingPagination = false;
        this.fetchOrders();
      });
    },
    fetchOrders() {
      if (!this.normalizedShopId) {
        this.error = 'Không có ShopID để tải danh sách đơn hàng.';
        this.items = [];
        this.totalRows = 0;
        return;
      }

      this.loading = true;
      this.error = '';

      const param = {
        pagination: this.pagination,
        filter: [{ column: 'ShopID', value: this.normalizedShopId, condition: 's_equal' }],
        quickFilter: [],
      };
      const header = {
        headers: request_header(this.token),
      };
      const query = request_payload(param);

      this.$http.get(`${configs.API}/admin/api/main_order/${query}`, header).then(
        res => {
          const payload = res.data || {};
          this.items = payload.data || [];
          this.totalRows = payload.total || 0;
          this.loading = false;
        },
        () => {
          this.loading = false;
          this.items = [];
          this.totalRows = 0;
          this.error = 'Không thể tải danh sách đơn hàng cùng shop.';
        }
      );
    },
    getDisplayId(item) {
      return item && item.ID ? `MD${item.ID}` : '—';
    },
    getStatusColor(status) {
      const match = configs.ORDER_STATUS.find(item => item.value === status);
      return match ? match.color : '#888';
    },
    openOrder(orderId) {
      const route = this.$router.resolve({ name: 'OrderDetail', params: { Pid: orderId } });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style scoped>
.shop-orders-link {
  color: #0088cc;
  font-weight: 700;
  text-decoration: underline;
}

.shop-orders-image-wrap {
  display: flex;
  align-items: center;
}

.shop-orders-image {
  border: 1px solid #ddd;
  border-radius: 4px;
}

.shop-orders-image-placeholder {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  color: #999;
  font-size: 11px;
  border: 1px dashed #ddd;
  border-radius: 4px;
}

.shop-orders-empty {
  padding: 16px 0;
  text-align: center;
  color: #777;
}
</style>
